import React, { Fragment, useEffect, useState } from "react";
import { RenderMaptilerTileset } from "./RenderMaptilerTileset";
import { RenderMapresource } from "./RenderMapresource";
import { Map, MapProps, TileLayer, MapControl, ZoomControl } from "react-leaflet";
import { parseMapLocation } from "../models/MapLocation";
import { RasterMap } from "./RasterMap";
import { RasterLayer } from "./RasterLayer";
import { useTranslation } from "react-i18next";
import { Segment } from "semantic-ui-react";
import { useBool } from "../../sg-hooks/useBool";
import FullscreenControl from "./FullscreenControl";

import Geolocate from "./Geolocate";

const getTextProps = (t: (key: string) => string) => ({
    failedToLoadMap: t("Kartan hakeminen epäonnistui"),
    failedToLoadMapresource: t("Kartan metatietoja ei löydy")
});


export interface MapAdapterProps extends MapProps {
    tilesetUrl: string;
    defaultPosition?: string;
    attribution?: string;
    locateControl?: boolean;
    controls?: boolean;
};

export const MapAdapter: React.FC<MapAdapterProps> = ({ tilesetUrl: nextTilesetUrl, defaultPosition, attribution, locateControl, controls = true, ...mapProps }) => {
    const texts = getTextProps(useTranslation().t);

    const parsedPosition = defaultPosition ? parseMapLocation(defaultPosition) : {} as any;

    const defaultZoom = parsedPosition?.zoom || mapProps.zoom;
    const defaultCenter: [number, number] | undefined = parsedPosition?.center ? parsedPosition?.center : mapProps.center as [number, number];

    const [isReady, setReady, clearReady] = useBool(true);
    const [tilesetUrl, setTilesetUrl] = useState<string>(nextTilesetUrl);

    const [waiting, setWaiting] = useState(true);

    useEffect(() => {
        if (waiting === true) {
            setTimeout(() => {
                setWaiting(false)
            }, 175)
        }
    }, [waiting])

    useEffect(() => {
        if (nextTilesetUrl !== tilesetUrl) {
            setTilesetUrl("");
            clearReady();


            let tilesetUpdate = setTimeout(() => {
                setTilesetUrl(nextTilesetUrl);
            }, 300);

            return () => { clearTimeout(tilesetUpdate); };
        }
    }, [nextTilesetUrl]);


    const renderControls = () => (
        <Fragment>
            <ZoomControl position="topright" />
            <FullscreenControl position="topright" />
        </Fragment>
    )

    if (!tilesetUrl || waiting) {
        return (
            <Segment className={mapProps.className} style={mapProps.style} loading basic />
        );
    };


    if (tilesetUrl.indexOf("maptiler") > -1) {
        return (
            <RenderMaptilerTileset
                tilesetUrl={tilesetUrl}
                render={({ tileset }) => {
                    if (!tileset) return <span children={texts.failedToLoadMap} />;
                    if (!tileset.mapresource) return <span children={texts.failedToLoadMapresource} />;

                    return (
                        <RasterMap
                            boundingBox={tileset.mapresource.boundingBox}
                            {...mapProps}
                            zoom={defaultZoom}
                            center={defaultCenter}
                            whenReady={setReady}
                            zoomControl={false}
                            attributionControl={controls}


                        >
                            <RasterLayer
                                mapresource={tileset.mapresource}
                                url={tilesetUrl}
                                attribution={attribution}
                                noWrap
                            />

                            {controls && renderControls()}
                            {isReady ? mapProps.children : null}
                        </RasterMap>
                    );
                }}
            />
        );
    };

    if (tilesetUrl.indexOf("systemsgarden.com/modules/tilannekuva") > -1) {
        return (
            <RenderMapresource
                tilesetUrl={tilesetUrl}
                render={({ mapresource }) => {
                    if (!mapresource) return <span children={texts.failedToLoadMapresource} />;
                    return (
                        <RasterMap
                            boundingBox={mapresource.boundingBox}
                            {...mapProps}
                            zoom={defaultZoom}
                            center={defaultCenter}
                            whenReady={setReady}
                            zoomControl={false}
                            attributionControl={controls}
                        >
                            <RasterLayer
                                mapresource={mapresource}
                                url={tilesetUrl}
                                attribution={attribution}
                                noWrap
                            />

                            {controls && renderControls()}
                            {isReady ? mapProps.children : null}
                        </RasterMap>
                    );
                }}
            />
        );
    };


    return (
        <Map
            key={tilesetUrl}
            {...mapProps}
            zoom={defaultZoom}
            center={defaultCenter}
            whenReady={setReady}
            zoomControl={false}
            attributionControl={controls}
        >
            <TileLayer
                url={tilesetUrl}
                attribution={attribution
                    //  || tilesetUrl.indexOf("osm") > -1 ? '&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors' : attribution
                }
                noWrap
            />

            {controls && renderControls()}

            {locateControl && tilesetUrl.indexOf("openstreetmap") > -1 && <Geolocate
                position="topright"
                icon="map marker alternate icon leaflet-locate-icon"
                iconLoading="spinner loading icon leaflet-locate-icon"
                iconElementTag="i"
                //  showCompass
                flyTo={false}
                onLocationError={(err: any) => console.log(err)}
            />}

            {isReady ? mapProps.children : null}
        </Map>
    )
};